import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDatepicker } from '@angular/material/datepicker';
import { ActivatedRoute, Router } from '@angular/router';
import { forkJoin } from 'rxjs';
import { ApiService } from 'src/app/shared/services/api.service';
import { ToastrMsgService } from 'src/app/shared/services/shared-services/toastr.service';

type SelectOption = { label: string; value: number };
type ProjectRecord = { id: number; project_name?: string; domain?: string };

@Component({
  selector: 'app-add-siv',

  templateUrl: './add-siv.component.html',
  styleUrl: './add-siv.component.css'
})
export class AddSIVComponent implements OnInit {
  sivFrom!: FormGroup;
  selectedDomain = 'Residential'; // default
  sourceSrvId: number | null = null;
  sourceSrvReference = '';
  private pendingSrvRow: Record<string, unknown> | null = null;
   domainOptions:any = [];
  projectSelectOptions: SelectOption[] = [];
  projectRecords: ProjectRecord[] = [];
  staffSelectOptions: SelectOption[] = [];
  optionsMap: { [key: string]: any[] } = {
    domainOptionsDrp: this.domainOptions,
    projectOptions: this.projectSelectOptions,
    staffOptions: this.staffSelectOptions,
  };

  domainFields: { [key: string]: any[] } = {
    Residential: [
      { key: 'tileTypeIssued', label: 'Tile Type Issued', type: 'text', required: true, value: '' },
      { key: 'paintQuantity', label: 'Paint Quantity', type: 'volume', required: true, value: '' },
    ],
    Commercial: [
      { key: 'hvacDuctsIssued', label: 'HVAC Ducts Issued', type: 'number', required: true, value: '' },
      { key: 'fireSafetyEquipment', label: 'Fire Safety Equipment', type: 'text', required: true, value: '' },
    ],
    Educational: [
      { key: 'labFurnitureIssued', label: 'Lab Furniture Issued', type: 'number', required: true, value: '' },
      { key: 'sportsGear', label: 'Sports Gear', type: 'text', required: true, value: '' },
    ],
    Water: [
      { key: 'pipeSectionsIssued', label: 'Pipe Sections Issued', type: 'number', required: true, value: '' },
      { key: 'valveUnits', label: 'Valve Units', type: 'number', required: true, value: '' },
    ],
    Power: [
      { key: 'cableRolls', label: 'Cable Rolls', type: 'number', required: true, value: '' },
      { key: 'transformerOil', label: 'Transformer Oil', type: 'volume', required: true, value: '' },
    ],
    Gas: [
      { key: 'weldingRods', label: 'Welding Rods', type: 'number', required: true, value: '' },
      { key: 'pressureGauges', label: 'Pressure Gauges', type: 'number', required: true, value: '' },
    ],
    Telecom: [
      { key: 'fiberCableSpools', label: 'Fiber Cable Spools', type: 'number', required: true, value: '' },
      { key: 'towerBolts', label: 'Tower Bolts', type: 'number', required: true, value: '' },
    ],
    Road: [
      { key: 'asphaltQuantity', label: 'Asphalt Quantity', type: 'weight', required: true, value: '' },
      { key: 'roadMarkingPaint', label: 'Road Marking Paint', type: 'volume', required: true, value: '' },
    ],
    Consultancy: [
      { key: 'stationeryIssued', label: 'Stationery Issued', type: 'text', required: true, value: '' },
      { key: 'fieldSurveyKits', label: 'Field Survey Kits', type: 'number', required: true, value: '' },
    ],
  };


  commonFields = [
    { key: 'issueVoucherId', label: 'Issue Voucher ID', type: 'text', required: true, value: '' },
    { key: 'project_common_id', label: 'Project Name', type: 'select', optionKey: 'projectOptions', required: true, value: '', onChange: (value: any) => this.onProjectSelected(value) },
    { key: 'issueDate', label: 'Issue Date', type: 'date', required: true, value: this.todayDate() },
    { key: 'issuedTo', label: 'Issued To', type: 'text', required: true, value: '' },
    { key: 'itemDescription', label: 'Item Description', type: 'text', required: true, value: '' },
    { key: 'quantityIssued', label: 'Quantity Issued', type: 'number', required: true, value: '' },
    { key: 'unitOfMeasure', label: 'Unit of Measure', type: 'text', required: true, value: '' },
    { key: 'storeLocation', label: 'Store Location', type: 'text', required: true, value: '' },
    { key: 'authorizedByStaffId', label: 'Authorized By', type: 'select', optionKey: 'staffOptions', required: true, value: '' },
    { key: 'inspectedByStaffId', label: 'Inspected By', type: 'select', optionKey: 'staffOptions', required: true, value: '' },
    { key: 'remarks', label: 'Remarks', type: 'text', required: false, value: '' },
    { key: 'domain', label: 'Domain', type: 'select', optionKey: 'domainOptionsDrp', required: true, value: 'Residential', onChange: (value: string) => this.onDomainChange(value) },
  ];

  pickerMap: { [key: string]: MatDatepicker<any> } = {};
  constructor(
    private fb: FormBuilder,
    private readonly apiService: ApiService,
    private readonly toastrMsgService: ToastrMsgService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) { }

 ngOnInit(): void {
  this.domainOptions = [];
  this.optionsMap['domainOptionsDrp'] = this.domainOptions;
  this.sivFrom  = this.fb.group({});
  this.commonFields.forEach(field => {
    const validators = field.required ? [Validators.required] : [];
    const initial = field.key === 'issueDate' ? this.todayDate() : (field.value ?? '');
    this.sivFrom.addControl(field.key, this.fb.control(initial, validators));
  });
  this.onDomainChange(this.selectedDomain);
  this.loadLookups();

  const srvIdRaw = this.route.snapshot.queryParamMap.get('srvId');
  const srvId = srvIdRaw != null ? Number(srvIdRaw) : NaN;
  if (!Number.isFinite(srvId)) {
    this.toastrMsgService.error('SIV must be generated from an SRV request');
    void this.router.navigate(['/home/srv-list']);
    return;
  }
  this.sourceSrvId = srvId;
  this.loadFromSrv(srvId);
}

  private todayDate(): Date {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }

  compareSelectValues = (a: unknown, b: unknown): boolean =>
    a != null && b != null && String(a) === String(b);

  private resolveSrvProjectId(row: Record<string, unknown>, payload: Record<string, unknown>): number | null {
    const candidates = [
      row['project_common_id'],
      payload['project_common_id'],
      payload['projectId'],
      payload['project_id'],
    ];
    for (const raw of candidates) {
      const id = Number(raw);
      if (Number.isFinite(id) && id > 0) return id;
    }
    const name = String(
      row['project_name_snapshot'] || payload['projectName'] || payload['project_name'] || ''
    ).trim().toLowerCase();
    if (!name) return null;
    const byOption = this.projectSelectOptions.find((o) => o.label.trim().toLowerCase() === name);
    if (byOption) return Number(byOption.value);
    const byRecord = this.projectRecords.find(
      (p) => String(p.project_name || '').trim().toLowerCase() === name
    );
    return byRecord?.id ?? null;
  }

  private ensureProjectOption(projectId: number, label: string): void {
    if (this.projectSelectOptions.some((o) => Number(o.value) === projectId)) return;
    const option = { label: label || `Project #${projectId}`, value: projectId };
    this.projectSelectOptions = [...this.projectSelectOptions, option];
    this.optionsMap['projectOptions'] = this.projectSelectOptions;
    if (!this.projectRecords.some((p) => p.id === projectId)) {
      this.projectRecords = [...this.projectRecords, { id: projectId, project_name: option.label }];
    }
  }

  private staffLabel(s: Record<string, unknown>): string {
    const fn = [s['first_name'], s['last_name']].filter(Boolean).join(' ').trim();
    const full = String(s['full_name'] || '').trim();
    if (full) return full;
    if (fn) return fn;
    const id = s['id'] ?? s['personnel_id'];
    return id != null ? `Staff #${id}` : '';
  }

  private loadFromSrv(srvId: number): void {
    this.apiService.getSrvSivRequest(srvId).subscribe({
      next: (res: { data?: Record<string, unknown> }) => {
        const row = res?.data;
        if (!row || String(row['request_type']).toUpperCase() !== 'SRV') {
          this.toastrMsgService.error('Invalid source SRV');
          void this.router.navigate(['/home/srv-list']);
          return;
        }
        if (row['linked_siv_id']) {
          this.toastrMsgService.info('An SIV has already been generated for this SRV');
          void this.router.navigate(['/home/siv-list']);
          return;
        }
        this.sourceSrvReference = String(row['reference_code'] || `SRV #${srvId}`);
        this.pendingSrvRow = row;
        this.tryApplySrvPrefill();
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Failed to load source SRV';
        this.toastrMsgService.error(msg);
        void this.router.navigate(['/home/srv-list']);
      },
    });
  }

  private lookupsReady = false;

  private tryApplySrvPrefill(): void {
    if (!this.lookupsReady || !this.pendingSrvRow) return;
    this.prefillFromSrv(this.pendingSrvRow);
    this.pendingSrvRow = null;
  }

  private prefillFromSrv(row: Record<string, unknown>): void {
    const payload =
      row['payload'] && typeof row['payload'] === 'object'
        ? (row['payload'] as Record<string, unknown>)
        : {};
    const domain = String(payload['domain'] || row['domain'] || this.selectedDomain).trim();
    const matchedDomain = Object.keys(this.domainFields).find(
      (k) => k.toLowerCase() === domain.toLowerCase()
    );
    const effectiveDomain = matchedDomain || domain;
    if (effectiveDomain) {
      this.sivFrom.get('domain')?.setValue(effectiveDomain);
      this.onDomainChange(effectiveDomain);
    }

    const projectId = this.resolveSrvProjectId(row, payload);
    const projectLabel = String(
      row['project_name_snapshot'] || payload['projectName'] || payload['project_name'] || ''
    ).trim();
    if (projectId != null) {
      this.ensureProjectOption(projectId, projectLabel);
      this.sivFrom.get('project_common_id')?.setValue(projectId);
      this.onProjectSelected(projectId);
    }

    const srvRef = String(row['reference_code'] || '').trim();
    this.sivFrom.patchValue({
      issueVoucherId: srvRef ? `SIV-${srvRef}` : '',
      issueDate: this.todayDate(),
      issuedTo: String(payload['receivedFrom'] || payload['received_from'] || ''),
      itemDescription: String(payload['itemDescription'] || payload['item_description'] || row['item_summary'] || ''),
      quantityIssued: payload['quantityReceived'] ?? payload['quantity_received'] ?? '',
      unitOfMeasure: String(payload['unitOfMeasure'] || payload['unit_of_measure'] || ''),
      storeLocation: String(payload['storeLocation'] || payload['store_location'] || ''),
      authorizedByStaffId: payload['authorized_by_staff_id'] ?? payload['authorizedByStaffId'] ?? '',
      inspectedByStaffId: payload['inspected_by_staff_id'] ?? payload['inspectedByStaffId'] ?? '',
      remarks: srvRef
        ? `Generated from SRV ${srvRef}${payload['remarks'] ? `. ${payload['remarks']}` : ''}`
        : String(payload['remarks'] || ''),
    });

    const domainKeys = (this.domainFields[effectiveDomain] || []).map((f) => f.key);
    for (const key of domainKeys) {
      if (payload[key] != null && payload[key] !== '' && this.sivFrom.get(key)) {
        this.sivFrom.get(key)?.setValue(payload[key]);
      }
    }
  }

  private loadLookups(): void {
    forkJoin({
      projects: this.apiService.getProjectListForDropdown({ limit: 200 }),
      staff: this.apiService.getStaff({ keyword: '' }),
    }).subscribe({
      next: ({ projects, staff }) => {
        const projectRows = (projects as { data?: Record<string, unknown>[] })?.data ?? [];
        const staffRows = (staff as { data?: Record<string, unknown>[] })?.data ?? [];
        this.projectRecords = projectRows.map((p) => ({
          id: Number(p['id']),
          project_name: String(p['project_name'] || ''),
          domain: p['domain'] != null ? String(p['domain']) : '',
        }));
        this.projectSelectOptions = projectRows.map((p) => ({
          label: String(p['project_name'] || `Project #${p['id']}`),
          value: Number(p['id']),
        }));
        this.staffSelectOptions = staffRows
          .map((s: Record<string, unknown>) => {
            const id = Number(s['id'] ?? s['personnel_id']);
            return { label: this.staffLabel(s), value: id };
          })
          .filter((o: SelectOption) => Number.isFinite(o.value));
        this.optionsMap['projectOptions'] = this.projectSelectOptions;
        this.optionsMap['staffOptions'] = this.staffSelectOptions;
        this.buildDomainOptionsFromProjects();
        this.lookupsReady = true;
        this.tryApplySrvPrefill();
      },
      error: () => {
        this.toastrMsgService.error('Failed to load projects or staff');
      },
    });
  }

  private resolveProjectName(id: unknown): string {
    const n = Number(id);
    if (!Number.isFinite(n)) return '';
    return this.projectSelectOptions.find((o) => o.value === n)?.label || '';
  }

  private resolveStaffName(id: unknown): string {
    const n = Number(id);
    if (!Number.isFinite(n)) return '';
    return this.staffSelectOptions.find((o) => o.value === n)?.label || '';
  }

  private normalizePayload(raw: Record<string, unknown>): Record<string, unknown> {
    const payload = { ...raw };
    const authId = payload['authorizedByStaffId'];
    const inspId = payload['inspectedByStaffId'];
    payload['projectName'] = this.resolveProjectName(payload['project_common_id']);
    payload['authorizedBy'] = this.resolveStaffName(authId);
    payload['inspectedBy'] = this.resolveStaffName(inspId);
    delete payload['project_common_id'];
    delete payload['authorizedByStaffId'];
    delete payload['inspectedByStaffId'];
    if (Number.isFinite(Number(authId))) {
      payload['authorized_by_staff_id'] = Number(authId);
    }
    if (Number.isFinite(Number(inspId))) {
      payload['inspected_by_staff_id'] = Number(inspId);
    }
    const rd = payload['issueDate'];
    if (rd instanceof Date) {
      payload['issueDate'] = (rd as Date).toISOString().split('T')[0];
    }
    return payload;
  }

  onSubmit(): void {
    if (this.sivFrom.invalid) {
      this.sivFrom.markAllAsTouched();
      this.toastrMsgService.error('Please fill all required fields');
      return;
    }
    const raw = this.sivFrom.getRawValue() as Record<string, unknown>;
    const pidRaw = raw['project_common_id'];
    const project_common_id =
      pidRaw != null && pidRaw !== '' ? Number(pidRaw) : NaN;
    const payload = this.normalizePayload(raw);
    this.apiService.createSrvSivRequest({
      type: 'SIV',
      project_common_id: Number.isFinite(project_common_id) ? project_common_id : null,
      parent_srv_id: this.sourceSrvId,
      payload,
    }).subscribe({
      next: () => {
        this.toastrMsgService.success('SIV saved successfully');
        void this.router.navigate(['/home/siv-list']);
      },
      error: (err) => {
        const msg = err?.error?.message || err?.message || 'Save failed';
        this.toastrMsgService.error(msg);
      },
    });
  }
  onReset(): void {
    this.sivFrom.reset();
    this.sivFrom.get('issueDate')?.setValue(this.todayDate());
    if (this.sourceSrvId != null) {
      this.loadFromSrv(this.sourceSrvId);
    }
  }

  /**
   * Build the Domain dropdown from the unique domains found across the
   * loaded projects. Each option's value is normalized to the matching
   * `domainFields` key (case-insensitive) when one exists, so that
   * domain-specific fields can still render. If the currently selected
   * domain isn't part of the new list, snap it to the first option.
   */
  private buildDomainOptionsFromProjects(): void {
    const seen = new Set<string>();
    const opts: { value: string; label: string }[] = [];
    for (const p of this.projectRecords) {
      const raw = String(p.domain || '').trim();
      if (!raw) continue;
      const matched = Object.keys(this.domainFields).find(
        (k) => k.toLowerCase() === raw.toLowerCase()
      );
      const value = matched || raw;
      if (seen.has(value)) continue;
      seen.add(value);
      opts.push({ value, label: value });
    }
    opts.sort((a, b) => a.label.localeCompare(b.label));
    this.domainOptions = opts;
    this.optionsMap['domainOptionsDrp'] = this.domainOptions;

    if (opts.length === 0) return;
    const ctrl = this.sivFrom?.get('domain');
    const currentVal = ctrl?.value;
    if (!opts.some((o) => o.value === currentVal)) {
      const next = opts[0].value;
      ctrl?.setValue(next);
      this.onDomainChange(next);
    }
  }

  /**
   * When the user selects a project, look up its domain and auto-set the
   * Domain dropdown so the right domain-specific fields render. Falls back
   * silently if the project's domain is unknown to this form.
   */
  onProjectSelected(projectId: any): void {
    const id = Number(projectId);
    if (!Number.isFinite(id)) return;
    const proj = this.projectRecords.find((p) => p.id === id);
    const projDomain = String(proj?.domain || '').trim();
    if (!projDomain) return;
    const matchedKey = Object.keys(this.domainFields).find(
      (k) => k.toLowerCase() === projDomain.toLowerCase()
    );
    if (!matchedKey) return;
    if (this.sivFrom?.get('domain')?.value === matchedKey) return;
    this.sivFrom?.get('domain')?.setValue(matchedKey);
    this.onDomainChange(matchedKey);
  }

onDomainChange(domain: string) {
    this.selectedDomain = domain;
    Object.keys(this.sivFrom.controls).forEach(key => {
      if (key !== 'domain' && this.domainFields[domain]?.find(field => field.key === key) === undefined &&
        Object.values(this.domainFields).flat().some(field => field.key === key)) {
        this.sivFrom.removeControl(key);
      }
    });
    this.domainFields[domain].forEach(field => {
      this.sivFrom.addControl(field.key, this.fb.control(field.value));
    });

  }

}
